import React, { FC, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams, useRouteMatch, useHistory } from 'react-router-dom';
import { Flex, Result, Spin } from 'antd';

import { Dispatch, iRootState } from '../../../../../store';
import { paths } from '../../../../Root/paths';
import { useAsync } from '../../../../../hooks';
import { isSecurableTypesPayload, LoadedSecurableTypes } from '../../../../../utils/parseSecurableTypes';

import Content from '../../../../../components_/Content';

import useSecTreeItem from '../../useSecTreeItem';
import Form from './Form';

import '../PermissionsTable.css';

interface IParams {
  id: string;
}

type PermissionsRequest = {
  treeItem: string;
  status: 'loading' | 'ready' | 'error';
};

const mapState = (state: iRootState) => ({
  data: state.settingSecTree.data,
  roleItem: state.settingSecTree.roleItem,
  secTreeHierarchy: state.settingSecTree.secTreeHierarchy
});

const mapDispatch = (dispatch: any) => {
  const d = dispatch as Dispatch;

  return {
    getHierarchy: d.settingSecTree.getHierarchy,
    getRoleItem: d.settingSecTree.getRoleItem,
    getSecurableTypes: d.settingSecTree.getSecurableTypes,
    getNodeACL: d.settingSecTree.getNodeACL,
    updateOrCreate: d.settingSecTree.updateOrCreate,
    reset: d.settingSecTree.reset
  };
};

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

export type IProps = connectedProps;

const Index: FC<IProps> = ({
  data,
  roleItem,
  getRoleItem,
  updateOrCreate,
  getHierarchy,
  secTreeHierarchy,
  getSecurableTypes,
  getNodeACL,
  reset
}) => {
  const [runGetItem, isLoadingGetItem, isGetItemLoadingError] = useAsync(getRoleItem);
  const [runGetHierarchy, isLoadingGetHierarchy, isGetHierarchyError] = useAsync(getHierarchy);
  const [runGetNodeACL, isLoadingGetNodeACL, isGetHNodeACLError] = useAsync(getNodeACL);

  const [runUpdateOrCreate] = useAsync(updateOrCreate);
  const { id } = useParams<IParams>();
  const history = useHistory();
  const isCreate = Boolean(useRouteMatch(paths.settings.secTree.roles.create));
  const isEdit = Boolean(useRouteMatch(paths.settings.secTree.roles.edit()));
  const isCopy = Boolean(useRouteMatch(paths.settings.secTree.roles.copy()));

  const { treeItem, isTreeItemReady } = useSecTreeItem(secTreeHierarchy);
  const [loadedPermissions, setLoadedPermissions] = useState<LoadedSecurableTypes | null>(null);
  const [permissionsRequest, setPermissionsRequest] = useState<PermissionsRequest | null>(null);

  useEffect(() => {
    runGetHierarchy();
    if (isEdit || isCopy) {
      runGetItem(id);
      runGetNodeACL({ root: '', roleId: id });
    }
    return () => {
      reset();
    };
  }, []);

  // See Rights/index.tsx — local loadedPermissions avoids stale columns on fast tree navigation.
  useEffect(() => {
    if (!isTreeItemReady || !treeItem) {
      return;
    }

    let cancelled = false;
    const requestTreeItem = treeItem;

    setPermissionsRequest({ treeItem: requestTreeItem, status: 'loading' });

    const loadPermissions = async () => {
      try {
        const result = await getSecurableTypes(requestTreeItem);

        if (cancelled) {
          return;
        }

        if (isSecurableTypesPayload(result)) {
          setLoadedPermissions({
            treeItem: requestTreeItem,
            securableTypes: result.securableTypes
          });
          setPermissionsRequest({ treeItem: requestTreeItem, status: 'ready' });
          return;
        }

        // tslint:disable-next-line:no-console
        console.error('[SecTree/Roles] Failed to load securable types', {
          treeItem: requestTreeItem,
          error: result
        });
        setPermissionsRequest({ treeItem: requestTreeItem, status: 'error' });
      } catch (error) {
        if (cancelled) {
          return;
        }

        // tslint:disable-next-line:no-console
        console.error('[SecTree/Roles] Failed to load securable types', {
          treeItem: requestTreeItem,
          error
        });
        setPermissionsRequest({ treeItem: requestTreeItem, status: 'error' });
      }
    };

    loadPermissions();

    return () => {
      cancelled = true;
    };
  }, [getSecurableTypes, isTreeItemReady, treeItem]);

  const isPermissionsLoading =
    permissionsRequest?.treeItem === treeItem && permissionsRequest.status === 'loading';
  const isPermissionsError =
    permissionsRequest?.treeItem === treeItem && permissionsRequest.status === 'error';

  const handleCancel = () => {
    history.push(paths.settings.secTree.roles.root);
  };

  const isLoading = isLoadingGetItem || isLoadingGetHierarchy || isLoadingGetNodeACL;
  const isLoadingError = isGetItemLoadingError || isGetHierarchyError || isGetHNodeACLError;

  const title = isCreate
    ? 'Добавить роль'
    : isCopy
    ? 'Копировать роль'
    : isEdit && roleItem
    ? roleItem.friendlyName
    : '';

  return (
    <Content
      breadcrumb={[{ name: 'Настройки', url: paths.settings.secTree.roles.root }, { name: title }]}
      title={title}
    >
      {!isLoading && !isLoadingError && (
        <>
          <Form
            data={data}
            secTreeHierarchy={secTreeHierarchy}
            item={roleItem}
            isCreate={isCreate}
            isEdit={isEdit}
            isCopy={isCopy}
            treeItem={treeItem}
            loadedPermissions={loadedPermissions}
            isPermissionsLoading={isPermissionsLoading}
            isPermissionsError={isPermissionsError}
            onSubmitAction={runUpdateOrCreate}
            onCancel={handleCancel}
          />
        </>
      )}
      {isLoading && (
        <Flex justify="center" align="center" style={{ minHeight: 240 }}>
          <Spin size="large" />
        </Flex>
      )}
      {isLoadingError && <Result status="500" title="Ошибка сервера" />}
    </Content>
  );
};

const withConnect = connect(mapState, mapDispatch);

export default withConnect(Index);
